import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GuidedTour, type GuidedTourStep } from '@liscio/common';

import type { ClientPreviewTourCandidate } from 'modules/requests/components/ClientPreviewDialog/clientPreviewTourCandidates';
import {
  GUIDED_CUSTOM_STEP_LABEL,
  type GuidedCustomStepCommand,
  type GuidedCustomStepCommandSignal,
} from 'modules/requests/components/ClientPreviewDialog/customStepGuidedDemo';

const buildClientPreviewCandidateSteps = ({
  clientPreviewTourCandidates,
  onOpenClientPreview,
  onNavigateClientPreviewTourCandidate,
}: {
  clientPreviewTourCandidates: ClientPreviewTourCandidate[];
  onOpenClientPreview: () => void;
  onNavigateClientPreviewTourCandidate?: (
    candidate: ClientPreviewTourCandidate
  ) => void;
}): GuidedTourStep[] =>
  clientPreviewTourCandidates.flatMap((candidate): GuidedTourStep[] => {
    if (candidate.id !== 'signature') {
      return [
        {
          id: `client-preview-${candidate.id}`,
          targetId: candidate.targetId,
          title: candidate.title,
          body: candidate.body,
          placement: 'left',
          beforeShow: () => {
            onOpenClientPreview();
            onNavigateClientPreviewTourCandidate?.(candidate);
          },
        },
      ];
    }

    return [
      {
        id: 'client-preview-sign-transition',
        targetId: 'tax-delivery-client-preview-sign-step',
        title: 'Go to the signing step',
        body: 'Click Sign to see how the signing part of the client journey appears.',
        placement: 'bottom',
        nextLabel: 'Go to Sign',
        beforeShow: onOpenClientPreview,
        onNext: () => {
          onNavigateClientPreviewTourCandidate?.(candidate);
        },
      },
      {
        id: `client-preview-${candidate.id}`,
        targetId: candidate.targetId,
        title: candidate.title,
        body: candidate.body,
        placement: 'left',
        beforeShow: onOpenClientPreview,
      },
    ];
  });

export const buildTaxDeliveryTooltipTourSteps = ({
  onOpenClientPreview,
  onOpenCustomStepDialog,
  clientPreviewTourCandidates = [],
  onNavigateClientPreviewTourCandidate,
  onGuidedCustomStepCommand = () => Promise.resolve(true),
  onBackToDashboard = () => {},
}: {
  onOpenClientPreview: () => void;
  onOpenCustomStepDialog: () => void;
  onGuidedCustomStepCommand?: (command: GuidedCustomStepCommand) => Promise<boolean>;
  onBackToDashboard?: () => void;
  clientPreviewTourCandidates?: ClientPreviewTourCandidate[];
  onNavigateClientPreviewTourCandidate?: (
    candidate: ClientPreviewTourCandidate
  ) => void;
}): GuidedTourStep[] => [
  {
    id: 'delivery-details',
    targetId: 'tax-delivery-details',
    title: 'Review the delivery details',
    body: 'Liscio turns the processed return into a structured delivery draft, including balances, estimates, and review items.',
    placement: 'right',
  },
  {
    id: 'due-date',
    targetId: 'tax-delivery-due-date',
    title: 'Set the due date',
    body: 'Choose when the client should complete this delivery before sending it.',
    placement: 'left',
  },
  {
    id: 'assignees',
    targetId: 'tax-delivery-assignees',
    title: 'Choose assignees',
    body: 'Select the client contacts who should receive and complete this delivery.',
    placement: 'left',
  },
  {
    id: 'first-signer',
    targetId: 'tax-delivery-first-signer',
    title: 'Confirm the first signer',
    body: 'Pick the primary signer and choose whether KBA is required.',
    placement: 'left',
    optional: true,
  },
  {
    id: 'second-signer',
    targetId: 'tax-delivery-second-signer',
    title: 'Confirm the second signer',
    body: 'Choose the second signer, same-device signing, or KBA settings when needed.',
    placement: 'left',
    optional: true,
  },
  {
    id: 'estimate-reminders',
    targetId: 'tax-delivery-reminders',
    title: 'Choose estimate reminders',
    body: 'For estimated payments, choose whether clients should be reminded to pay, notified of a withdrawal, or not reminded.',
    placement: 'left',
  },
  {
    id: 'preview-button',
    targetId: 'tax-delivery-preview-button',
    title: 'Preview the client experience',
    body: 'Before sending, preview what your client will see so you can review the flow with confidence.',
    placement: 'left',
  },
  {
    id: 'client-preview',
    targetId: 'tax-delivery-client-preview',
    title: 'Review the client-facing flow',
    body: 'This preview shows the same steps your client will complete before the delivery is sent.',
    placement: 'top',
    beforeShow: onOpenClientPreview,
  },
  {
    id: 'client-preview-steps',
    targetId: 'tax-delivery-client-preview-steps',
    title: 'Confirm the client journey',
    body: 'Use the step list to review what your client will complete and in what order.',
    placement: 'left',
    beforeShow: onOpenClientPreview,
  },
  ...buildClientPreviewCandidateSteps({
    clientPreviewTourCandidates,
    onOpenClientPreview,
    onNavigateClientPreviewTourCandidate,
  }),
  {
    id: 'custom-step',
    targetId: 'tax-delivery-custom-step',
    title: 'Add a custom step',
    body: 'Use custom steps for filing instructions, invoices, or anything else your client should review or approve.',
    placement: 'left',
    beforeShow: onOpenClientPreview,
    onNext: onOpenCustomStepDialog,
  },
  {
    id: 'custom-step-label',
    targetId: 'tax-delivery-custom-step-label',
    title: 'Name the custom step',
    body: 'Type a label your client will understand, or use the suggested invoice label.',
    placement: 'left',
    beforeShow: onOpenCustomStepDialog,
    nextLabel: 'Use suggested label',
    onNext: () => onGuidedCustomStepCommand('fillLabel'),
  },
  {
    id: 'custom-step-instructions',
    targetId: 'tax-delivery-custom-step-text',
    title: 'Add client instructions',
    body: 'Write instructions for the client, or use the suggested payment instructions.',
    placement: 'left',
    beforeShow: onOpenCustomStepDialog,
    nextLabel: 'Use instructions',
    onNext: () => onGuidedCustomStepCommand('fillInstructions'),
  },
  {
    id: 'custom-step-upload',
    targetId: 'tax-delivery-custom-step-upload',
    title: 'Attach the sample file',
    body: 'Use the sample PDF to show how a file custom step appears in the client journey.',
    placement: 'left',
    beforeShow: onOpenCustomStepDialog,
    nextLabel: 'Use sample PDF',
    nextPendingLabel: 'Attaching...',
    onNext: () => onGuidedCustomStepCommand('attachSamplePdf'),
  },
  {
    id: 'custom-step-save',
    targetId: 'tax-delivery-custom-step-save',
    title: 'Save the custom step',
    body: 'Save the sample custom step so it appears in the client journey preview.',
    placement: 'left',
    beforeShow: onOpenCustomStepDialog,
    nextLabel: 'Save',
    nextPendingLabel: 'Saving...',
    onNext: () => onGuidedCustomStepCommand('save'),
  },
  {
    id: 'custom-step-created',
    targetId: 'tax-delivery-custom-step-created',
    title: 'Review the new client step',
    body: `Once saved, "${GUIDED_CUSTOM_STEP_LABEL}" appears in the client journey so you can confirm how the client will see it.`,
    placement: 'left',
    beforeShow: onOpenClientPreview,
    nextLabel: 'Open step',
    onNext: () => onGuidedCustomStepCommand('openCreatedStep'),
  },
  {
    id: 'custom-step-preview',
    targetId: 'tax-delivery-custom-step-preview',
    title: 'Review the custom step',
    body: 'This is how the custom invoice step appears in the client-facing preview.',
    placement: 'top',
    beforeShow: onOpenClientPreview,
    nextLabel: 'Back to dashboard',
    onNext: onBackToDashboard,
  },
];

type TaxDeliveryTooltipTourProps = {
  open: boolean;
  onClose: () => void;
  onOpenClientPreview: () => void;
  onOpenCustomStepDialog: () => void;
  clientPreviewTourCandidates?: ClientPreviewTourCandidate[];
  onNavigateClientPreviewTourCandidate?: (
    candidate: ClientPreviewTourCandidate
  ) => void;
  guidedCustomStepSavedSignal?: number;
  onGuidedCustomStepCommand?: (command: GuidedCustomStepCommand) => void;
  guidedCustomStepCommandCompletedSignal?: GuidedCustomStepCommandSignal | null;
  onBackToDashboard?: () => void;
  showDashboardReturnAction?: boolean;
};

export const TaxDeliveryTooltipTour = ({
  open,
  onClose,
  onOpenClientPreview,
  onOpenCustomStepDialog,
  clientPreviewTourCandidates = [],
  onNavigateClientPreviewTourCandidate,
  guidedCustomStepSavedSignal = 0,
  onGuidedCustomStepCommand = () => {},
  guidedCustomStepCommandCompletedSignal,
  onBackToDashboard = () => {},
  showDashboardReturnAction = false,
}: TaxDeliveryTooltipTourProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const customStepDialogOpenedRef = useRef(false);
  const pendingCommandRef = useRef<{
    command: GuidedCustomStepCommand;
    resolve: (advance: boolean) => void;
  } | null>(null);

  const handleOpenClientPreview = useCallback(() => {
    onOpenClientPreview();
  }, [onOpenClientPreview]);

  const handleOpenCustomStepDialog = useCallback(() => {
    onOpenClientPreview();
    if (customStepDialogOpenedRef.current) {
      return;
    }
    customStepDialogOpenedRef.current = true;
    onOpenCustomStepDialog();
  }, [onOpenClientPreview, onOpenCustomStepDialog]);

  const runGuidedCustomStepCommand = useCallback(
    (command: GuidedCustomStepCommand) => {
      onGuidedCustomStepCommand(command);
      return new Promise<boolean>((resolve) => {
        pendingCommandRef.current = { command, resolve };
      });
    },
    [onGuidedCustomStepCommand]
  );

  const steps = useMemo(
    () =>
      buildTaxDeliveryTooltipTourSteps({
        onOpenClientPreview: handleOpenClientPreview,
        onOpenCustomStepDialog: handleOpenCustomStepDialog,
        onGuidedCustomStepCommand: runGuidedCustomStepCommand,
        onBackToDashboard,
        clientPreviewTourCandidates,
        onNavigateClientPreviewTourCandidate,
      }),
    [
      clientPreviewTourCandidates,
      handleOpenClientPreview,
      handleOpenCustomStepDialog,
      onBackToDashboard,
      onNavigateClientPreviewTourCandidate,
      runGuidedCustomStepCommand,
    ]
  );

  useEffect(() => {
    if (open) {
      customStepDialogOpenedRef.current = false;
      setActiveStepIndex(0);
      return;
    }

    if (pendingCommandRef.current) {
      pendingCommandRef.current.resolve(false);
      pendingCommandRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || guidedCustomStepSavedSignal <= 0) {
      return;
    }

    const createdStepIndex = steps.findIndex(
      (step) => step.id === 'custom-step-created'
    );
    if (createdStepIndex >= 0) {
      setActiveStepIndex(createdStepIndex);
      if (pendingCommandRef.current?.command === 'save') {
        pendingCommandRef.current.resolve(false);
        pendingCommandRef.current = null;
      }
    }
  }, [guidedCustomStepSavedSignal, open, steps]);

  useEffect(() => {
    if (!guidedCustomStepCommandCompletedSignal || !pendingCommandRef.current) {
      return;
    }

    if (
      guidedCustomStepCommandCompletedSignal.command !==
      pendingCommandRef.current.command
    ) {
      pendingCommandRef.current.resolve(false);
      pendingCommandRef.current = null;
      return;
    }

    pendingCommandRef.current.resolve(
      guidedCustomStepCommandCompletedSignal.success !== false
    );
    pendingCommandRef.current = null;
  }, [guidedCustomStepCommandCompletedSignal]);

  return (
    <GuidedTour
      open={open}
      steps={steps}
      activeStepIndex={activeStepIndex}
      onStepChange={setActiveStepIndex}
      onSkip={onClose}
      onDone={onClose}
      returnAction={{
        label: 'Go to dashboard',
        onClick: onBackToDashboard,
        visible: showDashboardReturnAction,
      }}
    />
  );
};
